import { useState, useEffect } from "react";
import { useAuth, useDrive } from "../App";
import { Sidebar } from "./Dashboard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  Settings as SettingsIcon, 
  Cloud, 
  ExternalLink, 
  Lock, 
  Calculator, 
  Building2,
  Plus,
  Trash2,
  Users,
  UserPlus,
  X
} from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const { 
    initialized, 
    isAdmin, 
    businesses, 
    currentBusiness,
    addBusiness, 
    deleteBusiness,
    shareBusinessWithUser,
    getBusinessAccess,
    removeBusinessAccess,
    reloadBusinesses,
    folderIds,
    uploadFile,
    getFileAsJson,
    listFiles
  } = useDrive();
  
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Business management
  const [newBusinessName, setNewBusinessName] = useState("");
  const [addingBusiness, setAddingBusiness] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // User management
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [businessToShare, setBusinessToShare] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  
  // Business settings
  const [vatEnabled, setVatEnabled] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [savingBusiness, setSavingBusiness] = useState(false);

  // Load config on mount
  useEffect(() => {
    if (initialized && folderIds.config) {
      loadConfig();
    } else {
      setLoadingConfig(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds.config]);

  const loadConfig = async () => {
    try {
      const files = await listFiles(folderIds.config);
      const configFile = files.find(f => f.name === 'config.json');
      if (configFile) {
        const config = await getFileAsJson(configFile.id);
        setVatEnabled(config.business?.vatEnabled || false);
        setCompanyName(config.business?.companyName || "");
        setVatNumber(config.business?.vatNumber || "");
      }
    } catch (e) {
      console.log("No existing config found");
    } finally {
      setLoadingConfig(false);
    }
  };

  const saveBusinessSettings = async () => {
    if (!folderIds.config) {
      toast.error("Config folder not available");
      return;
    }
    
    setSavingBusiness(true);
    try {
      let config = {};
      try {
        const files = await listFiles(folderIds.config);
        const configFile = files.find(f => f.name === 'config.json');
        if (configFile) {
          config = await getFileAsJson(configFile.id);
        }
      } catch (e) {}
      
      config.business = {
        vatEnabled,
        companyName,
        vatNumber,
        updatedAt: new Date().toISOString()
      };
      config.updatedAt = new Date().toISOString();
      
      const files = await listFiles(folderIds.config);
      const configFile = files.find(f => f.name === 'config.json');
      
      await uploadFile('config.json', JSON.stringify(config, null, 2), 'application/json', folderIds.config, configFile?.id);
      
      toast.success("Business settings saved");
    } catch (error) {
      console.error("Error saving business settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleAddBusiness = async () => {
    if (!newBusinessName.trim()) {
      toast.error("Please enter a business name");
      return;
    }
    
    setAddingBusiness(true);
    try {
      await addBusiness(newBusinessName.trim());
      setNewBusinessName("");
      toast.success(`Business "${newBusinessName}" created successfully`);
    } catch (error) {
      toast.error(error.message || "Failed to create business");
    } finally {
      setAddingBusiness(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (!businessToDelete) return;
    
    setDeleting(true);
    try {
      await deleteBusiness(businessToDelete.id, businessToDelete.drive_folder_id);
      toast.success(`Business "${businessToDelete.name}" deleted`);
      setDeleteDialogOpen(false);
      setBusinessToDelete(null);
    } catch (error) {
      toast.error("Failed to delete business");
    } finally {
      setDeleting(false);
    }
  };

  const openShareDialog = async (business) => {
    setBusinessToShare(business);
    setShareDialogOpen(true);
    setLoadingPermissions(true);
    
    try {
      const accessList = await getBusinessAccess(business.id);
      setPermissions(accessList);
    } catch (error) {
      console.error("Failed to load access list:", error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleShare = async () => {
    if (!shareEmail.trim() || !businessToShare) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSharing(true);
    try {
      await shareBusinessWithUser(
        businessToShare.id, 
        shareEmail.trim(), 
        businessToShare.drive_folder_id
      );
      toast.success(`Shared with ${shareEmail}`);
      setShareEmail("");
      
      // Reload access list from backend
      const accessList = await getBusinessAccess(businessToShare.id);
      setPermissions(accessList);
    } catch (error) {
      toast.error("Failed to share. Check the email address.");
    } finally {
      setSharing(false);
    }
  };

  const handleRemoveAccess = async (access) => {
    if (!businessToShare) return;
    
    try {
      await removeBusinessAccess(
        businessToShare.id, 
        access.id, 
        access.drive_permission_id, 
        businessToShare.drive_folder_id
      );
      setPermissions(permissions.filter(p => p.id !== access.id));
      toast.success("Access removed");
    } catch (error) {
      toast.error("Failed to remove access");
    }
  };

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="settings" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="settings" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-4xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Settings
            </h1>
            <p className="text-slate-600 mt-1">
              {isAdmin ? "Manage businesses, users, and configuration" : "View your settings"}
            </p>
          </div>

          {/* Account Info */}
          <Card className="mb-6 bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <SettingsIcon className="w-5 h-5" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-medium text-slate-600">{user?.name?.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <p className="text-lg font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-slate-500">{user?.email}</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                    isAdmin ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {isAdmin ? "Admin" : "Staff"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Management - Admin Only */}
          {isAdmin && (
            <Card className="mb-6 bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Business Management
                </CardTitle>
                <CardDescription>Add businesses and manage user access. Each business gets its own folder structure.</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Info about folder structure */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-2">When you add a business, the following folders are created:</p>
                  <pre className="text-xs text-blue-700 font-mono bg-blue-100 p-2 rounded">
{`Accruey/
└─ [Business Name]/
    ├─ receipts/      (organized by YYYY-MM)
    ├─ bank_statements/
    ├─ exports/
    └─ vat_reports/`}
                  </pre>
                </div>

                {/* Add New Business */}
                <div className="flex gap-2 mb-6">
                  <Input
                    data-testid="new-business-name"
                    placeholder="Enter business name (e.g., ABC Trading Ltd)"
                    value={newBusinessName}
                    onChange={(e) => setNewBusinessName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBusiness()}
                    className="flex-1"
                  />
                  <Button 
                    data-testid="add-business-btn"
                    onClick={handleAddBusiness} 
                    disabled={addingBusiness || !newBusinessName.trim()}
                  >
                    {addingBusiness ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="ml-2">Creating...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span className="ml-2">Add Business</span>
                      </>
                    )}
                  </Button>
                </div>

                {/* Business List */}
                {businesses.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No businesses yet. Add your first business above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-500 font-medium mb-2">
                      Your Businesses ({businesses.length})
                    </p>
                    {businesses.map((business) => (
                      <div
                        key={business.id}
                        data-testid={`business-item-${business.name}`}
                        className={`p-4 rounded-lg border transition-all ${
                          currentBusiness?.id === business.id 
                            ? "bg-blue-50 border-blue-300" 
                            : "bg-slate-50 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              currentBusiness?.id === business.id ? "bg-blue-200" : "bg-slate-200"
                            }`}>
                              <Building2 className={`w-5 h-5 ${
                                currentBusiness?.id === business.id ? "text-blue-700" : "text-slate-600"
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{business.name}</p>
                              <p className="text-xs text-slate-500">
                                {currentBusiness?.id === business.id 
                                  ? "✓ Currently selected" 
                                  : "receipts/ • bank_statements/ • exports/ • vat_reports/"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`share-business-${business.name}`}
                              onClick={() => openShareDialog(business)}
                            >
                              <Users className="w-4 h-4 mr-1" />
                              Share
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-business-${business.name}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setBusinessToDelete(business);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Staff Access - Admin Only */}
          {isAdmin && (
            <Card className="mb-6 bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Staff Access
                </CardTitle>
                <CardDescription>Manage staff access to your businesses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 font-medium mb-2">How sharing works:</p>
                  <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                    <li>Click the <strong>"Share"</strong> button on any business above</li>
                    <li>Enter the staff member's Google email</li>
                    <li>They will get <strong>editor access</strong> to the business folder in Google Drive</li>
                    <li>They'll see the business when they log in as Staff</li>
                  </ol>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Staff permissions:</strong> Staff can upload receipts and view data for businesses shared with them. They cannot delete files or access other businesses.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Current Business Settings - Admin Only */}
          {isAdmin && currentBusiness && (
            <Card className="mb-6 bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  {currentBusiness.name} - VAT Settings
                </CardTitle>
                <CardDescription>Configure VAT tracking for this business</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingConfig ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* VAT Toggle */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Calculator className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">VAT Registered</p>
                          <p className="text-sm text-slate-500">Enable if this business is VAT registered</p>
                        </div>
                      </div>
                      <Switch
                        data-testid="vat-enabled-toggle"
                        checked={vatEnabled}
                        onCheckedChange={setVatEnabled}
                      />
                    </div>

                    {vatEnabled && (
                      <div className="grid gap-2 animate-in slide-in-from-top-2 duration-200">
                        <Label htmlFor="vat-number">VAT Registration Number</Label>
                        <Input
                          id="vat-number"
                          data-testid="vat-number-input"
                          value={vatNumber}
                          onChange={(e) => setVatNumber(e.target.value)}
                          placeholder="GB 123 4567 89"
                        />
                      </div>
                    )}

                    <Button 
                      data-testid="save-business-settings-btn"
                      onClick={saveBusinessSettings} 
                      disabled={savingBusiness}
                    >
                      {savingBusiness ? "Saving..." : "Save Settings"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Google Drive Info - Admin Only */}
          {isAdmin && (
          <Card className="mb-6 bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Cloud className="w-5 h-5" />
                Google Drive Storage
              </CardTitle>
              <CardDescription>All data is stored in Google Drive</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2">Folder Structure</p>
                  <pre className="text-sm text-slate-700 font-mono">
{`Accruey/
├─ config/              (admin settings)
├─ [Business Name]/
│   ├─ receipts/YYYY-MM/
│   ├─ bank_statements/
│   ├─ exports/
│   └─ vat_reports/`}
                  </pre>
                </div>
                <Button variant="outline" asChild>
                  <a href="https://drive.google.com" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Google Drive
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Privacy Info - Admin Only */}
          {isAdmin && (
          <Card className="bg-slate-900 text-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Your Data, Your Control</h3>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    <li>• All data is stored in your Google Drive</li>
                    <li>• Share businesses via Google Drive permissions</li>
                    <li>• Staff only see folders shared with them</li>
                    <li>• Delete anytime by removing the Accruey folder</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Business</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete <strong>{businessToDelete?.name}</strong>? 
            This will permanently delete all receipts, statements, and reports for this business.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteBusiness}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete Business"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Business Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share "{businessToShare?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Share this business with staff members. They'll be able to upload receipts and view data.
            </p>
            
            {/* Add User */}
            <div className="flex gap-2">
              <Input
                data-testid="share-email-input"
                type="email"
                placeholder="staff@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleShare()}
              />
              <Button onClick={handleShare} disabled={sharing}>
                {sharing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                <span className="ml-2">Add</span>
              </Button>
            </div>

            {/* Current Users */}
            <div>
              <Label className="text-sm text-slate-500 mb-2 block">Users with access:</Label>
              {loadingPermissions ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : permissions.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No staff members added yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {permissions.map((access) => (
                    <div key={access.id} className="flex items-center justify-between p-2 bg-slate-50 rounded" data-testid={`access-item-${access.user_email}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{access.user_name || access.user_email}</p>
                        <p className="text-xs text-slate-500">{access.user_email} &middot; {access.role}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveAccess(access)}
                        data-testid={`remove-access-${access.user_email}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
